import React from 'react';

// todo this lib should not be shared (= it should be explicitly be depended by 3 specific object table instances)
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { useSearchQuery } from '@neptune/object-table-search-business-logic';
import { useNameSearchQuery } from '@neptune/object-table-search-domain';
// todo npt-13160: move this lib outside of shared
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { SearchQuery, SearchQueryModelConverter } from '@neptune/search-query-domain';
import { useColumnsContext } from '@neptune/shared/columns-business-logic';
import { LeaderboardContext } from '@neptune/shared/entity-leaderboard-domain';
import {
  useExperimentsOnlyOptions,
  useGroupBy,
  useLeaderboardPagination,
  useLeaderboardPolling,
  useSortOptions,
} from '@neptune/shared/leaderboard-business-logic';
import {
  LeaderboardGrouping,
  LeaderboardQueryNameAliases,
  searchLeaderboard,
  SearchLeaderboardRequest,
} from '@neptune/shared/leaderboard-domain';
import { NameSearchMode } from '@neptune/shared/search-domain';

import { buildSearchLeaderboardMetaQuery } from './buildSearchLeaderboardQuery';
import { getEntityLeaderboardAttributesToFetch } from './entity-leaderboard-attributes-to-fetch';

type UseEntityLeaderboardPollingParams = {
  queryNameAliases?: LeaderboardQueryNameAliases;
  grouping?: LeaderboardGrouping;
  selectedExperimentNames?: string[];
  selectedRunIds?: string[];
  enabled?: boolean;
  projectIdentifier: string;
};

export const useEntityLeaderboardPolling = ({
  queryNameAliases,
  grouping,
  selectedRunIds,
  selectedExperimentNames,
  enabled,
  projectIdentifier,
}: UseEntityLeaderboardPollingParams) => {
  const { id, types, isArchived } = React.useContext(LeaderboardContext);

  const { columns } = useColumnsContext();
  const { searchQueryConvertedToLegacyName, mode, searchQuery } = useNameSearchQuery();
  const { metaQuery } = useSearchQuery();
  const [sorting] = useSortOptions();
  const { pagination } = useLeaderboardPagination(id);
  const [experimentsOnlyEnabled] = useExperimentsOnlyOptions();
  const { offset, limit } = pagination;

  const queryPagination = React.useMemo(() => ({ offset, limit }), [offset, limit]);

  const baseQuery = React.useMemo(
    () => buildSearchLeaderboardMetaQuery(isArchived, selectedExperimentNames, selectedRunIds),
    [isArchived, selectedExperimentNames, selectedRunIds],
  );

  const properSearchQuery =
    mode === NameSearchMode.REGEX ? searchQuery : searchQueryConvertedToLegacyName;

  const query: SearchQuery = React.useMemo(() => {
    const isEmptyQuery = ({ criteria }: SearchQuery) => criteria.length > 0;
    const currentModeQuery: SearchQuery = {
      criteria: [metaQuery, properSearchQuery].filter(isEmptyQuery),
      operator: 'and',
    };

    return metaQuery.criteria.length || properSearchQuery.criteria.length
      ? { criteria: [baseQuery, currentModeQuery], operator: 'and' }
      : baseQuery;
  }, [baseQuery, metaQuery, properSearchQuery]);

  const attributesToFetch = React.useMemo(
    () =>
      getEntityLeaderboardAttributesToFetch({
        columns,
        grouping,
      }),
    [columns, grouping],
  );

  const sortOptionsHash = JSON.stringify(sorting);
  const groupingHash = JSON.stringify(grouping);
  const searchParams: SearchLeaderboardRequest = React.useMemo(
    () => ({
      projectIdentifier,
      query: SearchQueryModelConverter.convertSearchQueryToNql(query),
      queryNameAliases,
      type: types,
      grouping,
      sorting,
      pagination: queryPagination,
      attributesToFetch,
      experimentsOnly: experimentsOnlyEnabled,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      projectIdentifier,
      query,
      types,
      groupingHash,
      sortOptionsHash,
      queryPagination,
      attributesToFetch,
      experimentsOnlyEnabled,
    ],
  );
  const { updateGroupCompare } = useGroupBy();

  const resolver = React.useCallback(
    async (query: SearchLeaderboardRequest) => {
      const result = await searchLeaderboard(query);

      if (query.grouping && query.grouping.compareIds && result.groups) {
        const openedGroupIds = result.groups.map(({ id }) => id);

        updateGroupCompare(
          query.grouping?.groupBy,
          query.grouping.compareIds,
          query.query,
          openedGroupIds,
        );
      }

      return result;
    },
    [updateGroupCompare],
  );

  return useLeaderboardPolling({
    id,
    query: searchParams,
    resolver,
    enabled,
  });
};
